import React, { useEffect, useState, useRef } from "react";
import { Client, ClientType } from "../../types/client.type";
import { api } from "../../api/api";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Menu,
  message,
  Modal,
  Select,
  Space,
  Table,
  Typography,
  DatePicker,
  Spin,
} from "antd";
import { DownOutlined, BarChartOutlined } from "@ant-design/icons";
import { Currency } from "../../types/currency.type";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import CreateTransaction from "../../components/CreateTransaction/createTransaction.component";
import useGetRequest from "../../hooks/useGetRequest";
import { Cashbox } from "../../types/cashbox.type";
import { PiMicrosoftExcelLogoFill } from "react-icons/pi";
import { useDownloadFile } from "../../hooks/useDownloadFile";
import { useAuth } from "../../contexts/auth.context";
import { canEditClients, canDeleteClients } from "../../utils/roleUtils";
import { User, UserRole } from "../../types/user.type";
import { roundToThreeDecimals, formatNumber } from "../../utils/roundToThreeDecimals";
import { useExchangeRate } from "../../contexts/exchangeRate.context";
import { crmApi } from "../../api/crmApi";
import dayjs from "dayjs";
import { useQueryParams } from "../../hooks/useQueryParams";

const { Title } = Typography;
const { Search } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

function printClientsList(clients: Client[], typeName: string) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const totalDebt = clients.reduce((acc, c) => acc + (c.totalClientDebt || 0), 0);
  const debtorsCount = clients.filter(c => (c.totalClientDebt || 0) > 0).length;

  const tableHtml = `
    <table class="print-table" cellspacing="0" cellpadding="0">
      <thead>
        <tr>
          <th>№</th>
          <th>Имя</th>
          <th>Телефон</th>
          <th>Валюта</th>
          <th>Долг клиента</th>
          <th>Наш долг</th>
        </tr>
      </thead>
      <tbody>
        ${clients.map((client, idx) => `
          <tr>
            <td>${idx + 1}</td>
            <td>${client.name || '&nbsp;'}</td>
            <td>${client.phone || '&nbsp;'}</td>
            <td>${client.debtCurrency || '&nbsp;'}</td>
            <td>${roundToThreeDecimals(client.totalClientDebt) || '0'}</td>
            <td>${roundToThreeDecimals(client.totalCompanyDebt) || '0'}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
  `;

  printWindow.document.write(`
    <html>
      <head>
        <title>Список клиентов</title>
        <style>
          * {
            margin: 0; padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 10px;
            background: #fff;
            padding: 12px;
          }
          .print-table {
            border-collapse: collapse;
            width: 100%;
            background: #fff;
          }
          .print-table th,
          .print-table td {
            border: 1px solid #000;
            padding: 4px 8px;
            text-align: left;
          }
          .print-table th {
            background-color: #f0f0f0;
          }
          .print-table tr:nth-child(even) {
            background-color: #fafafa;
          }

          @media print {
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .print-table th {
              background-color: #f0f0f0 !important;
            }
            .print-table td, .print-table th {
              border: 1px solid #000 !important;
            }
          }
        </style>
      </head>
      <body>
        <h2 style="font-size:12px; margin-bottom:4px;">Список клиентов: ${typeName}</h2>
        <div style="margin-bottom:6px; font-size:11px;"><b>Общий долг клиентов:</b> ${roundToThreeDecimals(totalDebt)}</div>
        <div style="margin-bottom:10px; font-size:11px;"><b>Количество клиентов-должников:</b> ${debtorsCount}</div>
        ${tableHtml}
      </body>
    </html>
  `);

  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
}


export default function Clients() {
  const { authState } = useAuth();
  const { exchangeRate } = useExchangeRate();
  const { type } = useParams();
  const location = useLocation();
  const searchInputRef = useRef<any>(null);
  const queryParams = useQueryParams();
  
  let typeName: string;
  switch (type) {
    case "purchase":
      typeName = "Покупка";
      break;
    case "sale":
      typeName = "Продажа";
      break;
    case "scrap":
      typeName = "Лом";
      break;
    default:
      typeName = "Неизвестный тип";
  }

  const { data: cashboxesList, loading: cashboxListLoading } =
    useGetRequest(`/cashbox/list`);
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [confirmLoading, setConfirmLoading] = useState<boolean>(false);
  
  // Инициализация состояний из URL query параметров
  const [searchTerm, setSearchTerm] = useState<string>(queryParams.getParam("search") || "");
  const [sortBy, setSortBy] = useState<string>(queryParams.getParam("sortBy") || "createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">((queryParams.getParam("sortOrder") as "asc" | "desc") || "desc");
  const [currency, setCurrency] = useState<Currency | undefined>(queryParams.getParam("currency") as Currency || undefined);
  const [page, setPage] = useState<number>(queryParams.getParamAsNumber("page", 1));
  const [pageSize, setPageSize] = useState<number>(queryParams.getParamAsNumber("pageSize", 10));
  const [total, setTotal] = useState<number>(0);
  const [clientHasDebt, setClientHasDebt] = useState<boolean>(queryParams.getParamAsBoolean("clientHasDebt"));
  const [companyHasDebt, setCompanyHasDebt] = useState<boolean>(queryParams.getParamAsBoolean("companyHasDebt"));
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isPrepaidModalOpen, setIsPrepaidModalOpen] = useState<boolean>(false);
  const [isTransactionModalOpen, setIsTransactionModalOpen] =
    useState<boolean>(false); 
  const [isDebtsModalOpen, setIsDebtsModalOpen] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [form] = Form.useForm();
  const [transactionForm] = Form.useForm();
  const [prepaidForm] = Form.useForm();
  const [debtsForm] = Form.useForm();
  const { loading: downloadLoading, downloadFile } = useDownloadFile();
  const [auditReportLoading, setAuditReportLoading] = useState(false);
  
  // Состояния для отчетов клиентов
  const [isReportModalOpen, setIsReportModalOpen] = useState<boolean>(false);
  const [reportClient, setReportClient] = useState<Client | null>(null);
  const [reportDateRange, setReportDateRange] = useState<[string, string] | null>(null);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  const [clientOrders, setClientOrders] = useState<any[]>([]);
  const [clientTransactions, setClientTransactions] = useState<any[]>([]);
  const [reportSummary, setReportSummary] = useState<any>(null);

  const [stats, setStats] = useState<any>(null);

  // Продажники для привязки клиентских транзакций
  const [salesUsers, setSalesUsers] = useState<User[]>([]);

  // Синхронизация состояний с URL query параметрами
  useEffect(() => {
    const params: Record<string, string | number | boolean | undefined> = {};
    
    if (searchTerm) params.search = searchTerm;
    if (sortBy !== "createdAt") params.sortBy = sortBy;
    if (sortOrder !== "desc") params.sortOrder = sortOrder;
    if (currency) params.currency = currency;
    if (page !== 1) params.page = page;
    if (pageSize !== 10) params.pageSize = pageSize;
    if (clientHasDebt) params.clientHasDebt = true;
    if (companyHasDebt) params.companyHasDebt = true;
    
    queryParams.setParams(params);
  }, [searchTerm, sortBy, sortOrder, currency, page, pageSize, clientHasDebt, companyHasDebt]);

  // Обработка поиска из Command Palette
  useEffect(() => {
    const state = location.state as { searchQuery?: string; clientType?: string; forceRefresh?: number };
    
    if (state?.searchQuery) {
      // Проверяем, нужно ли перейти на другой тип клиентов
      if (state.clientType && state.clientType !== type) {
        // Переходим на правильный тип клиентов
        navigate(`/clients/${state.clientType}`, { 
          state: { searchQuery: state.searchQuery, forceRefresh: state.forceRefresh },
          replace: true 
        });
        return;
      }
      
      // Устанавливаем поисковый запрос
      setSearchTerm(state.searchQuery);
      setPage(1); // Сбрасываем страницу на первую
      
      // Очищаем state после использования
      window.history.replaceState({}, document.title);
      
      // Устанавливаем фокус на поле поиска
      setTimeout(() => {
        searchInputRef.current?.focus();
        searchInputRef.current?.select(); // Выделяем текст для удобства
      }, 200);
    }
  }, [location.pathname, location.state, type, navigate]); // Добавляем зависимость от location.state для отслеживания изменений

  useEffect(() => {
    fetchClients();
  }, [
    page,
    pageSize,
    searchTerm,
    sortBy,
    sortOrder,
    clientHasDebt,
    companyHasDebt,
    currency,
    type,
  ]);

  useEffect(() => {
    if (exchangeRate) {
      form.setFieldsValue({ exchangeRate });
    }
  }, [exchangeRate, form]);

  useEffect(() => {
    // Получаем статистику клиентов
    api.get('/stats/clients').then(res => {
      setStats(res.data);
    });
  }, []);

  useEffect(() => {
    // Загружаем список продажников для выбора в транзакциях клиента
    crmApi
      .getSalesUsers()
      .then((users) => setSalesUsers(users))
      .catch((err) => {
        console.error("Ошибка при загрузке продажников", err);
    });
  }, []);

  const fetchClients = async () => {
    setLoading(true);

    try {
      const response = await api.get("/client", {
        params: {
          page,
          limit: pageSize,
          search: searchTerm,
          sortBy,
          order: sortOrder,
          clientHasDebt: clientHasDebt ? true : undefined,
          companyHasDebt: companyHasDebt ? true : undefined,
          currency,
          clientType: typeName,
        },
      });
      setClients(response.data.clients);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching clients", error);
      message.error("Ошибка при получение клиентов.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleAddClient = () => {
    form.resetFields();
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  const handleEditClient = (client: Client) => {
    form.setFieldsValue(client);
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleSubmitForm = async (values: Partial<Client>) => {
    setConfirmLoading(true);

    try {
      if (selectedClient) {
        await api.put(`/client/${selectedClient._id}`, values);
        message.success("Клиент успешно обновлен.");
      } else {
        await api.post("/client", values);
        message.success("Клиент успешно добавлен.");
      }
      fetchClients();
      form.resetFields();
      setIsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error saving client", error);
      message.error(error.response.data.error);
    }
  };

  const handleTransactionClick = (client: Client) => {
    setSelectedClient(client);
    setIsTransactionModalOpen(true);
    transactionForm.resetFields();
  };

  const handlePrepaidClick = (client: Client) => {
    setSelectedClient(client);
    setIsPrepaidModalOpen(true);
    prepaidForm.resetFields();
  };

  const handleDebtsClick = (client: Client) => {
    setSelectedClient(client);
    debtsForm.setFieldsValue({
      totalClientDebt: client.totalClientDebt,
      totalCompanyDebt: client.totalCompanyDebt,
    });
    setIsDebtsModalOpen(true);
  };

  const handleExport = () => {
    downloadFile("/export/clients", "clients_data.xlsx", {
      clientType: typeName,
    });
  };

  const handleAuditReport = async () => {
    setAuditReportLoading(true);
    try {
      await downloadFile('/client/audit-report', `audit_report_${typeName}.xlsx`, {
        clientType: typeName,
      });
    } finally {
      setAuditReportLoading(false);
    }
  };

  const columns = [
    {
      title: "Имя",
      dataIndex: "name",
      key: "name",
      fixed: "left" as "left",
    },
    {
      title: "Телефон",
      dataIndex: "phone",
      key: "phone",
    },
    {
      title: "Валюта долга",
      dataIndex: "debtCurrency",
      key: "debtCurrency",
    },
    {
      title: "Долг клиента",
      dataIndex: "totalClientDebt",
      key: "totalClientDebt",
      render: (data: number, record: Client) => `${roundToThreeDecimals(data)} ${record.debtCurrency}`,
    },
    {
      title: "Наш долг",
      key: "totalCompanyDebt",
      dataIndex: "totalCompanyDebt",
      render: (data: number, record: Client) => `${roundToThreeDecimals(data)} ${record.debtCurrency}`,
    },
    {
      title: "Создан",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => {
        const date = new Date(createdAt);
        const formattedDate = date.toLocaleDateString("ru-RU");
        return <p>{formattedDate}</p>;
      },
    },
    {
      title: "Создал",
      dataIndex: "ownerName",
      key: "ownerName",
    },
    {
      title: "",
      key: "actions",
      render: (_: any, client: Client) => {
        const menu = (
          <Menu>
            {/* Действия, требующие прав записи */}
            {authState.user?.role !== UserRole.SalesDept && canEditClients(authState.user?.role) && (
              <>
                <Menu.Item onClick={() => handleTransactionClick(client)}>
                  {typeName === "Продажа" ? "Оплата от клиента" : "Оплатить"}
                </Menu.Item>
                <Menu.Item onClick={() => handlePrepaidClick(client)}>
                  Возврат предоплаты
                </Menu.Item>
              </>
            )}
            
            {/* Действия только для просмотра (GET запросы) */}
            {authState.user?.role !== UserRole.SalesDept && (
              <>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${client._id}/client/${client.name}/USD&TJS/transactions`
                    )
                  }
                >
                  Смотреть транзакции
                </Menu.Item>
                <Menu.Item
                  onClick={() =>
                    navigate(
                      `/${client._id}/client/${client.clientType}/${client.name}/orders`
                    )
                  }
                >
                  Заказы
                </Menu.Item>
              </>
            )}

            <Menu.Item onClick={() => handleOpenReportModal(client)}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              Отчет
            </Menu.Item>
            
            {canEditClients(authState.user?.role) && (
              <Menu.Item onClick={() => handleEditClient(client)}>
                Изменить
              </Menu.Item>
            )}

            {authState.user?.role === UserRole.Admin && (
              <Menu.Item onClick={() => handleDebtsClick(client)}>
                Изменить долги
              </Menu.Item>
            )}
            
            {canDeleteClients(authState.user?.role) && (
              <Menu.Item 
                onClick={() => handleDeleteClient(client)}
                style={{ color: '#ff4d4f' }}
              >
                Удалить
              </Menu.Item>
            )}
          </Menu>
        );

        return (
          <Dropdown overlay={menu}>
            <Button type="link">
              Действия <DownOutlined />
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  const handleCreateTransacttion = async (values: {
    amount: number;
    cashboxId: string;
    exchangeRate: number;
    description: string;
    machineId: string;
    salesUserId?: string;
  }) => {
    setConfirmLoading(true);
    try {
      const data = {
        ...values,
        exchangeRate: exchangeRate || values.exchangeRate,
      };

      await api.post(`/client/transaction/${selectedClient?._id}`, data);
      message.success("Транзакция успешно создана.");

      fetchClients();
      setIsTransactionModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating transaction", error);
      message.error(error.response.data.error);
    }
  };

  const handleReturnPrepaid = async (values: {
    amount: number;
    exchangeRate: number;
    cashboxId: string;
    description: string;
    currency: Currency;
    salesUserId?: string;
  }) => {
    setConfirmLoading(true);
    try {
            const data = {
          ...values,
          exchangeRate: exchangeRate || values.exchangeRate,
      };
      await api.post(`/client/return-prepaid/${selectedClient?._id}`, data);
      message.success("Возврат преоплаты успешн создан.");

      fetchClients();
      setIsPrepaidModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error creating prepaid", error);
      message.error(error.response.data.error);
    }
  };

  const handleUpdateDebts = async (values: {
    totalClientDebt: number;
    totalCompanyDebt: number;
  }) => {
    setConfirmLoading(true);
    try {
      await api.put(`/client/${selectedClient?._id}/debts`, values);
      message.success("Долги клиента успешно обновлены.");
      fetchClients();
      setIsDebtsModalOpen(false);
      setConfirmLoading(false);
    } catch (error: any) {
      setConfirmLoading(false);
      console.error("Error updating debts", error);
      message.error(error.response?.data?.error || "Ошибка при обновлении долгов.");
    }
  };

  // Функция для открытия модального окна отчетов
  const handleOpenReportModal = async (client: Client) => {
    setReportClient(client);
    setIsReportModalOpen(true);
    setReportDateRange(null);
    setClientOrders([]);
    setClientTransactions([]);
    setReportSummary(null);
  };

  // Функция для закрытия модального окна отчетов
  const handleCloseReportModal = () => {
    setIsReportModalOpen(false);
    setReportClient(null);
    setClientOrders([]);
    setClientTransactions([]);
    setReportSummary(null);
  };

  // Функция для удаления клиента
  const handleDeleteClient = async (client: Client) => {
    Modal.confirm({
      title: "Удалить клиента",
      content: `Вы уверены, что хотите удалить клиента "${client.name}"? Клиент будет перемещен в архив.`,
      okText: "Удалить",
      okType: "danger",
      cancelText: "Отмена",
      onOk: async () => {
        setConfirmLoading(true);
        try {
          await api.delete(`/client/${client._id}`);
          message.success("Клиент успешно удален и перемещен в архив.");
          fetchClients();
        } catch (error: any) {
          console.error("Error deleting client", error);
          message.error(error.response?.data?.error || "Ошибка при удалении клиента.");
        } finally {
          setConfirmLoading(false);
        }
      },
    });
  };

  // Функция для форматирования даты
  const toDDMMYY = (dateStr: string) => {
    if (!dateStr) return "";
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}.${mm}.${yyyy.slice(2)}`;
  };

  // Функция для форматирования периода
  const formatPeriod = (range: [string, string] | null) => {
    if (!range || !range[0] || !range[1]) return '';
    const [start, end] = range;
    const toHuman = (d: string) => {
      if (!d) return '';
      const [yyyy, mm, dd] = d.split('-');
      return `${dd}.${mm}.${yyyy}`;
    };
    return `Период: ${toHuman(start)} — ${toHuman(end)}`;
  };
  return (
    <div style={{ padding: 24 }}>
      <Title>Клиенты {typeName}</Title>
      <Space style={{ marginBottom: 16 }}>
        <Search
          ref={searchInputRef}
          placeholder="Поиск по названию"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onSearch={handleSearch}
          allowClear
        />
        <Select
          placeholder="Сортировать по"
          value={sortBy}
          onChange={(value) => {
            setSortBy(value);
            setPage(1);
          }}
          style={{ width: 150 }}
        >
          <Option value="createdAt">Дата</Option>
          <Option value="name">Название</Option>
          <Option value="totalSoldAmount">Продано (сумма)</Option>
          <Option value="totalPurchasedAmount">Куплено (сумма)</Option>
          <Option value="totalReceivedAmount">Получено денег</Option>
          <Option value="totalPaidAmount">Заплачено денег</Option>
        </Select>
        <Select
          value={sortOrder}
          onChange={(value: "asc" | "desc") => {
            setSortOrder(value);
            setPage(1);
          }}
          style={{ width: 120 }}
        >
          <Option value="asc">По возрастанию</Option>
          <Option value="desc">По убыванию</Option>
        </Select>
        <Checkbox
          checked={clientHasDebt}
          onChange={(e) => {
            setClientHasDebt(e.target.checked);
            setPage(1);
          }}
        >
          Клиент имеет долг
        </Checkbox>

        <Checkbox
          checked={companyHasDebt}
          onChange={(e) => {
            setCompanyHasDebt(e.target.checked);
            setPage(1);
          }}
        >
          У нас есть долг
        </Checkbox>
        {canEditClients(authState.user?.role) && (
          <Button type="primary" onClick={handleAddClient}>
            Добавить
          </Button>
        )}
        <Button
          type="default"
          loading={downloadLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleExport}
        >
          Экспорт в Excel
        </Button>
        <Button
          type="default"
          loading={auditReportLoading}
          icon={<PiMicrosoftExcelLogoFill />}
          onClick={handleAuditReport}
        >
          Получить отчет ревизии
        </Button>
        <Button
          type="default"
          onClick={() => printClientsList(clients, typeName)}
        >
          Печать
        </Button>
      </Space>
      <Table
        columns={columns}
        dataSource={clients}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
      />
      <Modal
        title={selectedClient ? "Изменить клиента" : "Добавить клиента"}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={form} onFinish={handleSubmitForm} layout="vertical">
          <Form.Item name="clientType" initialValue={typeName}>
            <Select
              placeholder="Выберите тип клиента"
              value={typeName}
              disabled={selectedClient != null}
            >
              {Object.values(ClientType).map((type) => (
                <Option key={type} value={type}>
                  {type}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="name"
            label="Имя"
            rules={[{ required: true, message: "Введите имя" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="phone"
            label="Телефон"
            rules={[{ required: true, message: "Введите телефон" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="debtCurrency"
            label="Валюта долга"
            rules={[{ required: true, message: "Выберите валюту долга" }]}
            initialValue={Currency.US_DOLLAR}
          >
            <Select placeholder="Выберите валюту">
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="maxDiscount" label="Скидка лимит %">
            <InputNumber min={0} max={100} />
          </Form.Item>

          <Form.Item name="tag" label="Тег (для статистики)">
            <Input placeholder="Введите тег (необязательно)" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={"Возврат предоплаты"}
        open={isPrepaidModalOpen}
        onCancel={() => setIsPrepaidModalOpen(false)}
        onOk={() => prepaidForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={prepaidForm} layout="vertical" onFinish={handleReturnPrepaid}>
          <Form.Item
            name="cashboxId"
            label="Касса"
            rules={[{ required: true, message: "Выберите кассу" }]}
          >
            <Select placeholder="Выберите кассу" loading={cashboxListLoading}>
              {cashboxesList?.map((cashbox: Cashbox) => (
                <Option key={cashbox._id} value={cashbox._id}>
                  Имя: {cashbox.name} | Валюта: {cashbox.currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="currency"
            label="Валюта"
            rules={[{ required: true, message: "Выберите валюту" }]}
          >
            <Select
              placeholder="Выберите валюту"
              onChange={(value) => setCurrency(value)}
            >
              {Object.values(Currency).map((currency) => (
                <Option key={currency} value={currency}>
                  {currency}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="amount"
            label="Сумма"
            rules={[{ required: true, message: "Введите сумму" }]}
          >
            <InputNumber min={0} />
          </Form.Item>

          <Form.Item
            name="exchangeRate"
            label="Курс"
            hidden={true}
          >
            <InputNumber min={0} />
          </Form.Item>
          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: "Введите описание." }]}
          >
            <Input.TextArea />
          </Form.Item>
          {/* Продажник для привязки возврата к балансу продажника */}
          {salesUsers.length > 0 && (
            <Form.Item
              name="salesUserId"
              label="Продажник"
              rules={[{ required: true, message: "Выберите продажника" }]}
            >
              <Select placeholder="Выберите продажника">
                {salesUsers
                  .filter((u) => u.role === UserRole.SalesDept)
                  .map((user) => (
                    <Option key={user._id} value={user._id}>
                      {user.name} ({user.username})
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {selectedClient && (
        <CreateTransaction
          title="Создать транзакцию"
          open={isTransactionModalOpen}
          form={transactionForm}
          confirmLoading={confirmLoading}
          onCancel={() => setIsTransactionModalOpen(false)}
          onOk={() => transactionForm.submit()}
          onFinish={handleCreateTransacttion}
          salesUsers={salesUsers}
        />
      )}

      {/* Модальное окно изменения долгов */}
      <Modal
        title="Изменить долги клиента"
        open={isDebtsModalOpen}
        onCancel={() => setIsDebtsModalOpen(false)}
        onOk={() => debtsForm.submit()}
        confirmLoading={confirmLoading}
      >
        <Form form={debtsForm} layout="vertical" onFinish={handleUpdateDebts}>
          <Form.Item
            name="totalClientDebt"
            label={`Долг клиента (${selectedClient?.debtCurrency || 'USD'})`}
            rules={[{ required: true, message: "Введите долг клиента" }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="totalCompanyDebt"
            label={`Наш долг (${selectedClient?.debtCurrency || 'USD'})`}
            rules={[{ required: true, message: "Введите долг компании" }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно отчетов клиента */}
      <Modal
        title={`Отчет клиента: ${reportClient?.name || ''}${reportDateRange && reportDateRange[0] && reportDateRange[1] ? ' (' + formatPeriod(reportDateRange) + ')' : ''}`}
        open={isReportModalOpen}
        onCancel={handleCloseReportModal}
        footer={null}
        width={1400}
      >
        <div style={{ marginBottom: 16 }}>
          <span>Период: </span>
          <RangePicker
            style={{ marginRight: 8 }}
            format="DD.MM.YY"
            value={reportDateRange && reportDateRange[0] && reportDateRange[1] ? [
              reportDateRange[0] ? dayjs(reportDateRange[0], "YYYY-MM-DD") : null,
              reportDateRange[1] ? dayjs(reportDateRange[1], "YYYY-MM-DD") : null
            ] : null}
            onChange={(dates, dateStrings) => {
              if (dates && dateStrings) {
                setReportDateRange([
                  dates[0]?.format("YYYY-MM-DD") || '',
                  dates[1]?.format("YYYY-MM-DD") || ''
                ]);
              } else {
                setReportDateRange(null);
              }
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            loading={reportLoading}
            onClick={async () => {
              if (!reportClient || !reportDateRange?.[0] || !reportDateRange?.[1]) return;
              setReportLoading(true);
              try {
                const startDate = toDDMMYY(reportDateRange[0]);
                const endDate = toDDMMYY(reportDateRange[1]);
                const res = await api.get(`/client/full-report/${reportClient._id}`, {
                  params: {
                    startDate: startDate,
                    endDate: endDate,
                  },
                });
                setClientOrders(res.data.orders);
                setClientTransactions(res.data.transactions);
                setReportSummary(res.data.summary);
              } catch (e) {
                message.error('Ошибка загрузки отчета');
              } finally {
                setReportLoading(false);
              }
            }}
          >
            Показать
          </Button>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => {
              // Печать отчета
              const printWindow = window.open('', '_blank');
              if (!printWindow) return;
              
              const ordersHtml = document.getElementById('client-orders-table')?.outerHTML || '';
              const transactionsHtml = document.getElementById('client-transactions-table')?.outerHTML || '';
              
              printWindow.document.write(`
                <html>
                  <head>
                    <title>Отчет клиента: ${reportClient?.name || ''}</title>
                    <style>
                      body, table, th, td { font-family: Arial, sans-serif; font-size: 8px !important; padding: 0 !important; margin: 0 !important; border: 1px solid #000 !important; }
                      th { background: #f5f5f5; }
                      h3 { margin-top: 16px; font-size: 11px; }
                      .summary { margin-top: 16px; font-size: 10px; font-weight: bold; }
                      .no-print { display: none !important; }
                    </style>
                  </head>
                  <body>
                    <h2 style="font-size:12px; margin:0 0 2px 0;">Отчет клиента: ${reportClient?.name || ''}</h2>
                    <div style="font-size:10px; margin-bottom:2px;">${formatPeriod(reportDateRange)}</div>
                    ${ordersHtml}
                    ${transactionsHtml}
                    <div class="summary">
                      <div>Общая сумма заказов: <b>${formatNumber(reportSummary?.totalOrdersAmountUSD)} USD / ${formatNumber(reportSummary?.totalOrdersAmountTJS)} TJS</b></div>
                      <div>Общая сумма транзакций: <b>${formatNumber(reportSummary?.totalTransactionsAmountUSD)} USD / ${formatNumber(reportSummary?.totalTransactionsAmountTJS)} TJS</b></div>
                      <div>Долг клиента: <b>${formatNumber(reportSummary?.clientDebtUSD)} USD / ${formatNumber(reportSummary?.clientDebtTJS)} TJS</b></div>
                      <div>Общая сумма заказов: <b>${formatNumber(reportSummary?.totalOrdersAmountUSD)} USD / ${formatNumber(reportSummary?.totalOrdersAmountTJS)} TJS</b></div>
                      <div>Общая сумма транзакций: <b>${formatNumber(reportSummary?.totalTransactionsAmountUSD)} USD / ${formatNumber(reportSummary?.totalTransactionsAmountTJS)} TJS</b></div>
                      <div>Количество заказов: <b>${clientOrders.length}</b></div>
                      <div>Количество транзакций: <b>${clientTransactions.length}</b></div>
                      <div>Долг клиента: <b>${formatNumber(reportSummary?.clientDebtUSD)} USD / ${formatNumber(reportSummary?.clientDebtTJS)} TJS</b></div>
                      <div>Долг компании: <b>${formatNumber(reportClient?.totalCompanyDebt)} USD</b></div>
                    </div>
                  </body>
                </html>
              `);
              printWindow.document.close();
              printWindow.focus();
              printWindow.print();
            }}
          >
            Печать
          </Button>
        </div>
        {reportLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin size="large" />
          </div>
        ) : clientOrders.length === 0 && clientTransactions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#888' }}>Нет данных за выбранный период</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {/* Заказы клиента */}
            {clientOrders.length > 0 && (
              <div>
                <h3>Заказы клиента</h3>
                <Table
                  id="client-orders-table"
                  dataSource={clientOrders.map((order, idx) => ({ ...order, key: order._id || idx }))}
                  pagination={false}
                  bordered
                  columns={[
                    { 
                      title: '№', 
                      key: 'index', 
                      width: 60,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    { title: 'Дата', dataIndex: 'createdAt', key: 'createdAt', render: (v) => v ? new Date(v).toLocaleDateString() : '' },
                    { title: 'Статус', dataIndex: 'status', key: 'status' },
                    { title: 'Количество', dataIndex: 'total', key: 'total', render: (v) => formatNumber(v) },
                    { title: 'Ед. изм.', dataIndex: 'unitOfMeasurement', key: 'unitOfMeasurement' },
                    { title: 'Цена USD', dataIndex: 'price', key: 'priceUSD', render: (v) => formatNumber(v?.USD) },
                    { title: 'Цена TJS', dataIndex: 'price', key: 'priceTJS', render: (v) => formatNumber(v?.TJS) },
                    { title: 'Сумма USD', dataIndex: 'totalCost', key: 'totalCostUSD', render: (v) => formatNumber(v?.USD) },
                    { title: 'Сумма TJS', dataIndex: 'totalCost', key: 'totalCostTJS', render: (v) => formatNumber(v?.TJS) },
                    { title: 'Валюта', dataIndex: 'currency', key: 'currency' },
                    { title: 'Описание', dataIndex: 'name', key: 'name' },
                  ]}
                  summary={pageData => {
                    const totalUSD = pageData.reduce((acc, order) => acc + (order.totalCost?.USD || 0), 0);
                    const totalTJS = pageData.reduce((acc, order) => acc + (order.totalCost?.TJS || 0), 0);
                    const totalQuantity = pageData.reduce((acc, order) => acc + (order.total || 0), 0);
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><b>{formatNumber(totalQuantity)}</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><b>{formatNumber(totalQuantity)}</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}></Table.Summary.Cell>
                        <Table.Summary.Cell index={5}></Table.Summary.Cell>
                        <Table.Summary.Cell index={6}></Table.Summary.Cell>
                        <Table.Summary.Cell index={7}><b>{formatNumber(totalUSD)} USD</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={8}><b>{formatNumber(totalTJS)} TJS</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={9}></Table.Summary.Cell>
                        <Table.Summary.Cell index={10}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </div>
            )}

            {/* Транзакции клиента */}
            {clientTransactions.length > 0 && (
              <div style={{ marginTop: 32 }}>
                <h3>Транзакции клиента</h3>
                <Table
                  id="client-transactions-table"
                  dataSource={clientTransactions.map((tx, idx) => ({ ...tx, key: tx._id || idx }))}
                  pagination={false}
                  bordered
                  columns={[
                    { 
                      title: '№', 
                      key: 'index', 
                      width: 60,
                      render: (_: any, __: any, index: number) => index + 1
                    },
                    { title: 'Дата', dataIndex: 'createdAt', key: 'createdAt', render: (v) => v ? new Date(v).toLocaleDateString() : '' },
                    { title: 'Тип', dataIndex: 'type', key: 'type' },
                    { title: 'Сумма', dataIndex: 'amount', key: 'amount', render: (v, record) => {
                      const amount = v || 0;
                      const exchangeRate = record.exchangeRate || 13;
                      const currency = record.currency;
                      
                      if (currency === 'USD') {
                        return `${formatNumber(amount)} USD / ${formatNumber(amount * exchangeRate)} TJS`;
                      } else if (currency === 'TJS') {
                        return `${formatNumber(amount / exchangeRate)} USD / ${formatNumber(amount)} TJS`;
                      }
                      return formatNumber(amount);
                    }},
                    { title: 'Валюта', dataIndex: 'currency', key: 'currency' },
                    { title: 'Касса', dataIndex: 'cashboxName', key: 'cashboxName' },
                    { title: 'Описание', dataIndex: 'description', key: 'description' },
                  ]}
                  summary={pageData => {
                    let totalUSD = 0;
                    let totalTJS = 0;
                    
                    pageData.forEach(tx => {
                      const amount = tx.amount || 0;
                      const exchangeRate = tx.exchangeRate || 13;
                      const currency = tx.currency;
                      
                      if (currency === 'USD') {
                        totalUSD += amount;
                        totalTJS += amount * exchangeRate;
                      } else if (currency === 'TJS') {
                        totalTJS += amount;
                        totalUSD += amount / exchangeRate;
                      }
                    });
                    
                    return (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}></Table.Summary.Cell>
                        <Table.Summary.Cell index={1}></Table.Summary.Cell>
                        <Table.Summary.Cell index={2}></Table.Summary.Cell>
                        <Table.Summary.Cell index={3}><b>{formatNumber(totalUSD)} USD / {formatNumber(totalTJS)} TJS</b></Table.Summary.Cell>
                        <Table.Summary.Cell index={4}></Table.Summary.Cell>
                        <Table.Summary.Cell index={5}></Table.Summary.Cell>
                        <Table.Summary.Cell index={6}></Table.Summary.Cell>
                      </Table.Summary.Row>
                    );
                  }}
                />
              </div>
            )}

            {/* Итоги */}
            {reportSummary && (
              <div style={{ marginTop: 16, padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <h4>Итоги за период ({reportSummary.periodDays} дней):</h4>
                <div>Общая сумма заказов: <b>{formatNumber(reportSummary.totalOrdersAmountUSD)} USD / {formatNumber(reportSummary.totalOrdersAmountTJS)} TJS</b></div>
                <div>Общая сумма транзакций: <b>{formatNumber(reportSummary.totalTransactionsAmountUSD)} USD / {formatNumber(reportSummary.totalTransactionsAmountTJS)} TJS</b></div>
                <div>Количество заказов: <b>{reportSummary.totalOrders}</b></div>
                <div>Количество транзакций: <b>{reportSummary.totalTransactions}</b></div>
                <div>Общая сумма заказов: <b>{formatNumber(reportSummary.totalOrdersAmountUSD)} USD / {formatNumber(reportSummary.totalOrdersAmountTJS)} TJS</b></div>
                <div>Общая сумма транзакций: <b>{formatNumber(reportSummary.totalTransactionsAmountUSD)} USD / {formatNumber(reportSummary.totalTransactionsAmountTJS)} TJS</b></div>
                <div>Количество заказов: <b>{clientOrders.length}</b></div>
                <div>Количество транзакций: <b>{clientTransactions.length}</b></div>
                <hr style={{ margin: '8px 0' }} />
                <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                  Долг клиента: <b>{formatNumber(reportSummary.clientDebtUSD)} USD / {formatNumber(reportSummary.clientDebtTJS)} TJS</b>
                </div>
                <div style={{ fontWeight: 'bold', color: '#52c41a' }}>
                  Долг компании: <b>{formatNumber(reportClient?.totalCompanyDebt)} USD</b>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
